import React, { useState, useMemo, useEffect } from 'react'
import {
  DesignedModal,
  GeneralSelect,
  selectAllValue,
  Button,
  useFrontEndEventTrackingService,
} from '@clickcease/cc-client-components-lib'
import { useTranslation } from 'react-i18next'
import { useSetMigrationMonitorMode } from 'Features/domainsManagement/api/setMigrationMonitorMode'
import { ModalDescription } from './ClickceaseMonitoringModeModal.styled'
import { useGetMigrationMonitorMode } from 'Features/domainsManagement/api/getMigrationMonitorMode'

let canShowModal = true
let refetchInterval: NodeJS.Timeout

export const ClickceaseMonitoringModeModal = () => {
  const { t } = useTranslation('domainManagement')
  const { track } = useFrontEndEventTrackingService()
  const { data: migrationDomainsList, refetch } = useGetMigrationMonitorMode(true)
  const [isOpen, setIsOpen] = useState(false)
  const [selectedDomains, setSelectedDomains] = useState<string[]>([])
  const { mutate, isLoading } = useSetMigrationMonitorMode()

  const domains = useMemo(
    () =>
      migrationDomainsList
        ?.filter((domain) => !domain.monitorMode && domain.paidMarketingConnected)
        .map((domain) => domain.address) ?? [],
    [migrationDomainsList]
  )

  useEffect(() => {
    if (domains.length) setIsOpen(canShowModal)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domains])

  useEffect(() => {
    const removeInterval = () => refetchInterval && clearInterval(refetchInterval)

    if (domains.length) refetchInterval = setInterval(() => refetch(), 30000) // 30 seconds
    else removeInterval()
    return () => removeInterval()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domains])

  const enableMonitoringMode = () => {
    const domainsToEnable = selectedDomains.filter((domain) => domain !== selectAllValue)
    mutate(domainsToEnable, {
      onSuccess: () => {
        track(['segment'], 'CC monitoring only applied', {})
        closeModal()
      },
    })
  }

  const translate = (key: string) => t(`clickcease_monitoring_mode_modal.${key}`)

  const closeModal = () => {
    canShowModal = false
    setIsOpen(false)
  }

  return (
    <DesignedModal
      title={translate('title')}
      isOpen={isOpen}
      style={{ width: '644px' }}
      onOpenChange={closeModal}
      disableAutoFocus
      actions={
        <Button
          text={translate('enable_button')}
          disabled={!selectedDomains.length}
          loading={isLoading}
          onClick={enableMonitoringMode}
        />
      }
    >
      <ModalDescription variant="baseRegular">{translate('description')}</ModalDescription>
      <GeneralSelect
        selectAll={domains.length > 1}
        wrapperCSS={{ width: '60%' }}
        isMulti
        topTitle
        value={selectedDomains.map((domain) => ({ label: domain, value: domain }))}
        options={domains?.map((domain) => ({ label: domain, value: domain }))}
        onChange={(e) => setSelectedDomains(e.map((domain) => domain.value))}
      />
    </DesignedModal>
  )
}
